/**
 * SEO utility functions for generating SEO-friendly metadata
 */

/**
 * Generate a URL-friendly slug from a name
 * @param name - The name to convert to a slug
 * @returns A URL-friendly slug
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Generate SEO title for a lawyer profile
 * @param name - Lawyer's name
 * @param title - Lawyer's professional title
 * @param emirate - Emirate location
 * @returns SEO-optimized title
 */
export function generateSeoTitle(name: string, title: string, emirate: string): string {
  return `${name} - ${title} in ${emirate} | UAE Lawyers Directory`;
}

/**
 * Generate SEO description for a lawyer profile
 * @param name - Lawyer's name
 * @param title - Lawyer's professional title
 * @param specializations - Array of specializations
 * @param experience - Years of experience
 * @param emirate - Emirate location
 * @returns SEO-optimized description
 */
export function generateSeoDescription(
  name: string,
  title: string,
  specializations: string[],
  experience: number,
  emirate: string
): string {
  const specsText = specializations.slice(0, 3).join(', ');
  return `${name} is a ${title} in ${emirate} with ${experience} years of experience. Specializing in ${specsText}. Contact now for legal consultation in UAE.`;
}

/**
 * Generate SEO keywords for a lawyer profile
 * @param name - Lawyer's name
 * @param title - Lawyer's professional title
 * @param specializations - Array of specializations
 * @param emirate - Emirate location
 * @param languages - Array of languages
 * @returns Array of SEO keywords
 */
export function generateSeoKeywords(
  name: string,
  title: string,
  specializations: string[],
  emirate: string,
  languages: string[]
): string[] {
  const keywords = [
    name,
    title,
    `${title} ${emirate}`,
    `lawyer ${emirate}`,
    `legal services ${emirate}`,
    'UAE lawyer',
    'Dubai lawyer',
    ...specializations.map(spec => `${spec} lawyer`),
    ...specializations.map(spec => `${spec} ${emirate}`),
    ...languages.map(lang => `${lang} speaking lawyer`),
  ];

  return [...new Set(keywords)]; // Remove duplicates
}

/**
 * Generate full SEO metadata for a lawyer
 * @param lawyer - Lawyer object
 * @returns Object containing slug, title, description, and keywords
 */
export function generateLawyerSeoMetadata(lawyer: {
  name: string;
  title: string;
  specializations: string[];
  experience: number;
  emirate: string;
  languages: string[];
}) {
  const slug = generateSlug(lawyer.name);
  const seoTitle = generateSeoTitle(lawyer.name, lawyer.title, lawyer.emirate);
  const seoDescription = generateSeoDescription(
    lawyer.name,
    lawyer.title,
    lawyer.specializations,
    lawyer.experience,
    lawyer.emirate
  );
  const seoKeywords = generateSeoKeywords(
    lawyer.name,
    lawyer.title,
    lawyer.specializations,
    lawyer.emirate,
    lawyer.languages
  );

  return {
    slug,
    seoTitle,
    seoDescription,
    seoKeywords,
  };
}